/**
 * El árbol condicional del bloque de sostenibilidad de Lift: qué se ve, qué se guarda
 * y cuándo se puede terminar.
 *
 * Espeja `SuitabilityProvider.ValidateSustainability` de la API, que **poda** las ramas
 * que el árbol no abre en vez de rechazarlas y sólo da un 400 si FALTA algo que sí
 * abre. Aquí se poda igual, y no por cortesía: lo que el front manda es lo que se
 * persiste en `SuitabilityTest.Answers`, así que dejar una respuesta huérfana de una
 * rama cerrada guardaría en el registro algo que el cliente no ha contestado.
 *
 * Misma duplicación consciente TS/C# que `gating.ts` (ADR-0002 §3, decisión 14).
 */
import {
  S1_SELF_DETERMINED_ORDER,
  S3_ADVERSE_IMPACT_ORDER,
  S3_SFDR_ORDER,
  S3_TAXONOMY_ORDER,
  type LiftAnswers,
  type LiftSustainabilityAnswers,
} from './answers';

/** Multiselección normalizada: sin repetidos y en orden, como la guarda el motor. */
const canonical = (orders: number[] | undefined): number[] =>
  [...new Set(orders ?? [])].sort((a, b) => a - b);

/** Sólo "quiero determinar tipología y proporción" abre el resto del bloque. */
export const isSelfDetermined = (value?: LiftSustainabilityAnswers): boolean =>
  value?.s1 === S1_SELF_DETERMINED_ORDER;

const opens = (
  value: LiftSustainabilityAnswers | undefined,
  characteristic: number,
): boolean => !!value?.s3?.includes(characteristic);

export const opensTaxonomy = (value?: LiftSustainabilityAnswers): boolean =>
  isSelfDetermined(value) && opens(value, S3_TAXONOMY_ORDER);

export const opensSfdr = (value?: LiftSustainabilityAnswers): boolean =>
  isSelfDetermined(value) && opens(value, S3_SFDR_ORDER);

export const opensAdverseImpact = (
  value?: LiftSustainabilityAnswers,
): boolean => isSelfDetermined(value) && opens(value, S3_ADVERSE_IMPACT_ORDER);

/**
 * Deja sólo lo que el árbol pide, o `undefined` si no se ha entrado al bloque.
 *
 * Es la función que hace que desmarcar una característica de S3 borre la respuesta
 * que había abierto, y que volver a S1 = neutro no arrastre lo contestado antes.
 */
export const pruneSustainability = (
  value: LiftSustainabilityAnswers | undefined,
): LiftSustainabilityAnswers | undefined => {
  if (value?.s1 === undefined) return undefined;

  if (!isSelfDetermined(value)) return { s1: value.s1 };

  const characteristics = canonical(value.s3);
  const pruned: LiftSustainabilityAnswers = { s1: value.s1, s3: characteristics };

  if (value.s2 !== undefined) pruned.s2 = value.s2;
  if (opensTaxonomy(value) && value.s4 !== undefined) pruned.s4 = value.s4;
  if (opensSfdr(value) && value.s5 !== undefined) pruned.s5 = value.s5;
  if (opensAdverseImpact(value)) pruned.s6 = canonical(value.s6);

  return pruned;
};

/**
 * Puerta del paso. El bloque es opcional (ADR-0002 §3, decisión 8): en blanco se puede
 * terminar el test. Pero **empezarlo obliga a acabarlo**, porque con S1 = "quiero
 * determinar" el motor exige el árbol entero y responde 400. Esta puerta es lo único
 * que impide mandarle un test que va a rechazar.
 */
export const isSustainabilityComplete = (answers: LiftAnswers): boolean => {
  const value = pruneSustainability(answers.sustainability);

  if (!isSelfDetermined(value)) return true;

  return (
    value?.s2 !== undefined &&
    !!value.s3?.length &&
    (!opensTaxonomy(value) || value.s4 !== undefined) &&
    (!opensSfdr(value) || value.s5 !== undefined) &&
    (!opensAdverseImpact(value) || !!value.s6?.length)
  );
};